import { useTranslation } from "react-i18next";
import GoalImg from "assets/icons/goal.svg?react";
import NotaIcon from "assets/icons/nota.svg?react";
import styles from "./styles.module.scss";
import Button from "components/Button";
import workspaceService from "api/workspaceService";
import { useNavigate, useParams } from "react-router-dom";
import { setWorkspaceId } from "utils/setWorkspaceId";
import { useEffect, useState } from "react";
import Input from "components/Input";
import { useAuth } from "react-oidc-context";
import { IUserCreated } from "dto/IUserCreated";
import InfoIcon from "assets/icons/error.svg?react";
import GmailIcon from "assets/icons/gmail.svg?react";
import OutlookIcon from "assets/icons/outlook.svg?react";
import AppleIcon from "assets/icons/apple.svg?react";
import AEmail from "assets/icons/aemail.svg?react";
import ArrowIcon from "assets/icons/arrowBack.svg?react";
import {
  User,
  UserManager,
} from "oidc-client-ts";
import Loading from "components/Loading";
import { Tooltip } from "react-tooltip";
import AuthLayout from "components/AuthLayout";
import { ESteps } from "components/Stepper";
import { ENotificationType, notification } from "utils/notification";
import { validatePasswordStrength } from "utils/validatePasswordStrength";

const InvitationModule = ({ notLogged }: { notLogged?: boolean }) => {
  const { t } = useTranslation();

  const steps = [
    {
      type: ESteps.INFORMATION,
      title: t("activation.stepper.yourData.title"),
      text: t("activation.stepper.yourData.description"),
    },
    {
      type: ESteps.KEY_GENERATION,
      title: t("activation.stepper.recoveryKey.title"),
      text: t("activation.stepper.recoveryKey.description"),
    },
  ];

  const [step, setStep] = useState(0);
  const { id, type } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const userManager = new UserManager(auth.settings);
  const {
    data: invitationData,
    isError,
    isLoading,
    error,
  } = workspaceService.useGetInvitation(id!);

  const { mutate: approve } = workspaceService.useApproveInvitation();
  const { mutate: switchWorkspace } = workspaceService.useWorkspaceSwitch();
  const { mutate: register } = workspaceService.useRegisterInWorkspace();

  const setUser = (accessToken: string, refreshToken: string) => {
    userManager.storeUser(
      new User({
        access_token: accessToken,
        refresh_token: refreshToken,
        token_type: "Bearer",
        profile: { sub: "", iss: "", aud: "", exp: 0, iat: 0 },
      }),
    );
  };

  const handleAccept = () => {
    console.log("Type ", type);
    if (notLogged) {
      if (!type) {
        auth.signinRedirect({
          state: { invitation: id },
        });
      }
      setStep(1);
      return;
    }

    approve(
      {
        Key: id,
      },
      {
        onSuccess: () => {
          if (invitationData?.data?.Case) {
            navigate("/");
            return;
          }

          switchWorkspace(
            {
              Id: invitationData?.data?.Workspace?.Id,
            },
            {
              onSuccess: () => {
                navigate("/");
                setWorkspaceId(invitationData?.data?.Workspace?.Id);
              },
            },
          );
        },
      },
    );
  };

  useEffect(() => {
    if (invitationData?.data?.Id) {
      handleAccept();
    }
  }, [invitationData]);

  const handleCreate = () => {
    register(
      {
        AcceptNotaRules: true,
        AcceptRules: true,
        Email: invitationData?.data?.Email,
        FirstName: invitationData?.data?.Name.split(" ")[0],
        InvitationId: id!,
        LastName: invitationData?.data?.Name.split(" ")[1] ?? "",
        Password: password,
        RepeatPassword: rePassword,
      },
      {
        onSuccess: (resp) => {
          const r = resp.data as IUserCreated;

          setUser(r.AccessToken, r.RefreshToken);
          auth.signinSilent();
          navigate("/");
        },
        onError: (e: any) => {
          const errors = e.response.data.errors;
          let mergedErrors = "";
          for (const key in errors) {
            if (errors.hasOwnProperty(key)) {
              mergedErrors += `${errors[key].join(",")} \n`;
            }
          }

          notification({
            type: ENotificationType.ERROR,
            title: t("errors.generic"),
            text: mergedErrors,
          });
        },
      },
    );
  };

  const [password, setPassword] = useState("");
  const [rePassword, setRePassword] = useState("");
  const [, setName] = useState("");
  const [, setInput] = useState(false);

  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [passwordWeak, setPasswordWeak] = useState(false);

  const [typ, setTyp] = useState(0);

  useEffect(() => {
    setPasswordMismatch(password && rePassword && password !== rePassword);
    setPasswordWeak(password && !validatePasswordStrength(password));
  }, [password, rePassword]);

  useEffect(() => {
    if (!type) {
      auth.signinRedirect({
        state: { invitation: id },
      });
      return;
    }
  }, [type]);

  useEffect(() => {
    if (!rePassword.length) {
      setInput(false);
      return;
    }

    if (password === rePassword) {
      setInput(false);
      return;
    }

    setInput(true);
  }, [password, rePassword]);

  const checkCan = () => {
    if (!password.length || !rePassword.length) {
      return true;
    }

    if (password !== rePassword) {
      return true;
    }

    return false;
  };

  if (isLoading) {
    return (
      <AuthLayout steps={steps} actualStep={0}>
        <>
          <Loading />
        </>
      </AuthLayout>
    );
  }

  if (isError) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.leftBar}>
          <div className={styles.logo}>
            <NotaIcon />
          </div>
          <div className={styles.text}>
            {t("activation.sidebarTagline")}
          </div>
          <div className={styles.img}>
            <GoalImg></GoalImg>
          </div>
          {/* <div className={styles.logout}>
            <HollowButton text="Wyloguj" onClick={() => {}}></HollowButton>
          </div> */}
        </div>
        <div className={styles.content}>
          <div className={styles.title}>
            {/* @ts-ignore */}
            {error?.response?.data?.errorCodes?.[0]?.message ??
              t("errors.invitationNotFound")}
          </div>
        </div>
      </div>
    );
  }

  const ChooseMethod = () => {
    return (
      <>
        <div className={styles.externalLogin}>
          <div
            onClick={() => {
              setTyp(1);
            }}
            className={styles.external}
          >
            <AEmail></AEmail> {t("auth.emailOrPassword")}
          </div>
          <a
            className={styles.external}
            onClick={() =>
              auth.signinRedirect({
                acr_values: "idp:Google",
                state: { invitation: id },
              })
            }
          >
            <GmailIcon></GmailIcon> {t("auth.loginWithGoogle")}
          </a>
          <a
            className={styles.external}
            onClick={() =>
              auth.signinRedirect({
                acr_values: "idp:Microsoft",
                state: { invitation: id },
              })
            }
          >
            <OutlookIcon></OutlookIcon> {t("auth.loginWithMicrosoft")}
          </a>
          <a
            className={styles.external}
            onClick={() =>
              auth.signinRedirect({
                acr_values: "idp:Apple",
                state: { invitation: id },
              })
            }
          >
            <AppleIcon></AppleIcon> {t("auth.loginWithApple")}
          </a>
        </div>
      </>
    );
  };

  if (notLogged && step === 1) {
    return (
      <div className={styles.wrapper}>
        <AuthLayout steps={steps} actualStep={0}>
          <>
            {typ === 1 && (
              <div
                onClick={() => {
                  setTyp(0);
                }}
                className={styles.back}
              >
                <ArrowIcon /> {t("common.backArrow")}
              </div>
            )}

            <div className={styles.title}>{t("activation.createAccount")}</div>
            <div className={styles.side}>{t("activation.startUsing")}</div>
            {typ === 0 && <ChooseMethod />}
            {typ === 1 && (
              <>
                <div className={styles.inputs}>
                  <Input
                    className={styles.input}
                    disabledTags
                    label={t("forms.fullName")}
                    value={invitationData?.data?.Name}
                    onChange={(v) => setName(v)}
                  />
                  <Input
                    className={styles.input}
                    label={t("forms.email")}
                    disabled
                    disabledTags
                    value={invitationData?.data?.Email ?? ""}
                    onChange={() => {}}
                  />
                  <div className={styles.box}>
                    <Input
                      className={styles.input}
                      id="new-password"
                      autoComplete="new-password"
                      label={t("auth.password")}
                      type="password"
                      disabledTags
                      value={password}
                      onChange={(v) => setPassword(v)}
                      allowToShowPassword={true}
                    />
                    {/* {input && (
                      <InfoIcon
                        data-tooltip-id={`bar-1`}
                        data-tooltip-content={t("validation.passwordMismatch")}
                        className={styles.error}
                      />
                    )} */}
                  </div>
                  <div className={styles.box}>
                    <Input
                      className={styles.input}
                      id="confirm-password"
                      autoComplete="new-password"
                      label={t("auth.repeatPassword")}
                      disabledTags
                      type="password"
                      value={rePassword}
                      onChange={(v) => setRePassword(v)}
                      allowToShowPassword={true}
                      RightIcon={
                        passwordMismatch && (
                          <InfoIcon
                            data-tooltip-id={`bar-1`}
                            data-tooltip-content={t("validation.passwordMismatch")}
                            title={t("validation.passwordMismatch")}
                            className={styles.error}
                          />
                        )
                      }
                    />
                  </div>

                  {passwordWeak && (
                    <div className={styles.errorBlock}>
                      {t("validation.weakPassword")}
                    </div>
                  )}

                  <div className={styles.hint}>
                    <i>
                      {t("validation.passwordRequirements")}
                    </i>
                  </div>

                  {passwordMismatch && (
                    <div className={styles.errorBlock}>
                      {t("validation.passwordsDoNotMatch")}
                    </div>
                  )}
                </div>
                <div className={styles.button}>
                  <Button
                    className={styles.b}
                    text={t("common.next")}
                    disabled={checkCan()}
                    onClick={() => handleCreate()}
                  />
                </div>
              </>
            )}
          </>
        </AuthLayout>
        {/* <div className={styles.leftBar}>
          <NewLogo className={styles.logo} />
        </div>
       
        </div> */}
        <Tooltip
          place="bottom"
          // style={{
          //   background: isOut ? '#BA1A1A' : '#5d49c7',
          // }}
          id={`bar-1`}
        />
      </div>
    );
  }

  return <></>;
};

export default InvitationModule;
